import React, { useState } from "react";
import { X, Check, MapPin, Phone, User, Home, Calendar } from "lucide-react";
import { BRAND, LOCALITIES } from "../mock";
import { useToast } from "../hooks/use-toast";

const serviceOptions = ["Dry Cleaning", "Wash & Iron", "Premium Laundry", "Shoe Cleaning", "Bag Cleaning", "Sofa Cleaning"];

const BookingModal = ({ open, onClose }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", mobile: "", locality: "", address: "", service: "Dry Cleaning", date: "",
  });

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid1 = form.name.trim() && /^\d{10}$/.test(form.mobile.trim());
  const valid2 = form.locality && form.address.trim();

  const submit = () => {
    const booking = { ...form, id: Date.now(), createdAt: new Date().toISOString() };
    const existing = JSON.parse(localStorage.getItem("laundryma_bookings") || "[]");
    localStorage.setItem("laundryma_bookings", JSON.stringify([booking, ...existing]));
    setStep(3);
    toast({ title: "Pickup scheduled!", description: "Our representative will call you shortly to confirm." });
  };

  const reset = () => {
    setStep(1);
    setForm({ name: "", mobile: "", locality: "", address: "", service: "Dry Cleaning", date: "" });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={reset}>
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden animate-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-navy px-6 py-5 relative">
          <button onClick={reset} className="absolute right-4 top-4 text-white/80 hover:text-white" aria-label="Close">
            <X size={22} />
          </button>
          <h3 className="text-white text-xl font-bold font-head">Book a Free Pickup</h3>
          <p className="text-white/70 text-sm mt-1">Doorstep pickup & delivery — absolutely free</p>
          {step < 3 && (
            <div className="flex gap-2 mt-4">
              {[1, 2].map((s) => (
                <div key={s} className={`h-1.5 flex-1 rounded-full ${step >= s ? "bg-orange-brand" : "bg-white/25"}`} />
              ))}
            </div>
          )}
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <Field icon={User} label="Your Name">
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name"
                  className="w-full outline-none bg-transparent text-sm" />
              </Field>
              <Field icon={Phone} label="Mobile Number">
                <input value={form.mobile} maxLength={10} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))}
                  placeholder="10-digit mobile" className="w-full outline-none bg-transparent text-sm" />
              </Field>
              <div>
                <label className="text-xs font-semibold text-navy/60 mb-1.5 block">Select Service</label>
                <div className="flex flex-wrap gap-2">
                  {serviceOptions.map((s) => (
                    <button key={s} onClick={() => set("service", s)}
                      className={`text-xs font-semibold px-3 py-2 rounded-full border transition-colors ${
                        form.service === s ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-gray-200 hover:border-navy"
                      }`}>{s}</button>
                  ))}
                </div>
              </div>
              <button disabled={!valid1} onClick={() => setStep(2)}
                className="w-full bg-orange-brand disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e0850f] text-white font-semibold py-3 rounded-xl transition-colors">
                Continue
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-navy/60 mb-1.5 block">Locality</label>
                <div className="flex items-center gap-2.5 border border-gray-200 rounded-xl px-3.5 py-3 focus-within:border-navy">
                  <MapPin size={18} className="text-orange-brand shrink-0" />
                  <select value={form.locality} onChange={(e) => set("locality", e.target.value)}
                    className="w-full outline-none bg-transparent text-sm text-navy">
                    <option value="">Choose your locality</option>
                    {LOCALITIES.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <Field icon={Home} label="Full Address">
                <input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="House / Flat, Street, Landmark"
                  className="w-full outline-none bg-transparent text-sm" />
              </Field>
              <Field icon={Calendar} label="Preferred Pickup Date">
                <input type="date" value={form.date} min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => set("date", e.target.value)} className="w-full outline-none bg-transparent text-sm text-navy" />
              </Field>
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 border border-gray-200 text-navy font-semibold py-3 rounded-xl hover:bg-gray-50 transition-colors">Back</button>
                <button disabled={!valid2} onClick={submit}
                  className="flex-1 bg-orange-brand disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#e0850f] text-white font-semibold py-3 rounded-xl transition-colors">
                  Book Pickup Now
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-navy font-head">Pickup Scheduled!</h4>
              <p className="text-navy/60 text-sm mt-2 mb-1">Thank you, <b>{form.name}</b>.</p>
              <p className="text-navy/60 text-sm mb-6">
                We'll pick up your <b>{form.service}</b> order. Our team will call <b>{form.mobile}</b> shortly to confirm.
              </p>
              <button onClick={reset} className="w-full bg-navy hover:bg-[#153a63] text-white font-semibold py-3 rounded-xl transition-colors">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, children }) => (
  <div>
    <label className="text-xs font-semibold text-navy/60 mb-1.5 block">{label}</label>
    <div className="flex items-center gap-2.5 border border-gray-200 rounded-xl px-3.5 py-3 focus-within:border-navy">
      <Icon size={18} className="text-orange-brand shrink-0" />
      {children}
    </div>
  </div>
);

export default BookingModal;
